import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CalendarModule } from 'primeng/calendar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { CommonBindingDataService } from '../../../../../../shared/services/common-binding-data.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { CommonModule, NgClass, NgStyle } from '@angular/common';
import { AppIcons } from '../../../../../../shared/app.icons';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { AppSettings } from 'app/modules/shared/app.settings';

interface ProofImageFile {
  signedUrl: string;
  originalFileName: string;
}

@Component({
  selector: 'app-order-info',
  standalone: true,
  imports: [CalendarModule, ButtonModule, DialogModule, TranslateModule, FormsModule, CommonModule, NgClass, NgStyle],
  templateUrl: './order-info.component.html',
  styleUrl: './order-info.component.scss'
})
export class OrderInfoComponent implements OnChanges {
  miIcons = AppIcons;
  @Input() showDispatchDateCalendar: boolean;
  @Input() pendingDateOfDispatchModel: Date;
  @Input() orderStatus: string;
  @Input() displayOrderStatus: string;
  @Input() attributeValues: any;
  @Input() details: any;
  @Input() attributeLabels: any[];
  @Input() orderStatusColor: string;
  @Input() protocolAttrValue: string;
  @Input() orderId: string;
  @Input() entityCode: string;
  @Output() confirmDateChange = new EventEmitter<Date>();
  openLogo: string = "../../../../../../assets/images/mi-theme/light/nav/pdf_icon.png";
  showConfirmDateDialog: boolean = false;
  proofImageFiles: ProofImageFile[] = [];

  constructor(
    public cs: CommonBindingDataService,
    public metaDataService: MetaDataService,
    public configService: ConfigService,
    public entityService: EntityService,
    private restApiService: RestApiService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['attributeValues'] || changes['orderStatus']) {
      this.loadProofImageFiles();
    }
  }

  private toFileIdArray(value: any): string[] {
    if (!value) return [];
    const arr: any[] = Array.isArray(value) ? [].concat(...value) : [String(value)];
    return arr.filter(Boolean).map(String);
  }

  private loadProofImageFiles() {
    this.proofImageFiles = [];
    if (this.orderStatus !== 'ORDER_PARTIALLY_RECEIVED') {
      return;
    }
    const fileIds = this.toFileIdArray(this.attributeValues?.proof_image);
    if (!fileIds.length) {
      const urls = this.attributeValues?.proof_image_url;
      if (Array.isArray(urls) && urls.length) {
        this.proofImageFiles = urls.map((url: string, index: number) => ({
          signedUrl: url,
          originalFileName: `Proof file ${index + 1}`
        }));
      }
      return;
    }
    this.restApiService.post(
      'get order proof image details',
      `${this.configService.getLoggedInTenantCode()}/api/v1/files/details`,
      {
        forTenantCode: this.configService.getForTenantCode(),
        uploadType: AppSettings.DOCUMENTS_TYPE.DOCUMENT,
        fileIds
      }
    ).subscribe({
      next: (fileDetails: any[]) => {
        const detailsMap = new Map<string, any>();
        if (Array.isArray(fileDetails)) {
          fileDetails.forEach(f => {
            if (f.fileId) detailsMap.set(f.fileId, f);
            if (f.resourceId) detailsMap.set(f.resourceId, f);
          });
        }
        this.proofImageFiles = fileIds.map((id, index) => {
          const detail = detailsMap.get(id);
          const fallbackUrl = this.attributeValues?.proof_image_url?.[index];
          return {
            signedUrl: detail?.signedUrl || fallbackUrl || '',
            originalFileName: detail?.originalFileName || `Proof file ${index + 1}`
          };
        }).filter(file => file.signedUrl);
      }
    });
  }

  downloadFile(url: string, fileName: string) {
    fetch(url, { method: 'GET' })
      .then(response => response.blob())
      .then(blob => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      });
  }

  downloadReturnReport() {
    this.entityService.downloadReturnOrderReport(this.entityCode, this.orderId).subscribe(response => {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(response.body);

      // Extract filename from content-disposition header
      let fileName = '';
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    });
  }

  onSaveDate() {
    this.showConfirmDateDialog = true;
  }

  onCancelDateDialog() {
    this.showConfirmDateDialog = false;
  }

  onConfirmDateChange() {
    this.showConfirmDateDialog = false;
    this.confirmDateChange.emit(this.pendingDateOfDispatchModel);
  }
}
